import { Box, Divider, Grid, Skeleton, TextField, Typography, IconButton, Tooltip } from '@mui/material';
import { useContext, useState } from 'react';
import { CampaignContext } from '../../contexts/CampaignContext';
import usePlatformCardColors from '../../hooks/usePlatformCardColors';
import { getMediaLogo } from '../../utils/file';
import eimojiGroup from '../../assets/telegram-emoji-group.png';
import { Social } from '../../interfaces/campaign';
import telegramChatInputImg from '../../assets/telegram-chat-input.svg';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/Download';

const IsSocialPreview = () => {
	const { t } = useTranslation();
	const { fetchedCampaign } = useContext(CampaignContext);
	const fetchedCampaignContents = fetchedCampaign?.contents[0];
	const { borderColorClass } = usePlatformCardColors(fetchedCampaign?.media!);
	const platform = fetchedCampaign?.media! as Social;
	const [copied, setCopied] = useState<{ field: 'description' | 'handle' | null }>({ field: null });

	const copyToClipboard = async (text: string | undefined, field: 'description' | 'handle') => {
		if (!text) return;
		try {
			await navigator.clipboard.writeText(text);
			setCopied({ field });
			setTimeout(() => setCopied({ field: null }), 1500);
		} catch (e) {
			const textarea = document.createElement('textarea');
			textarea.value = text;
			document.body.appendChild(textarea);
			textarea.select();
			try {
				document.execCommand('copy');
				setCopied({ field });
				setTimeout(() => setCopied({ field: null }), 1500);
			} finally {
				document.body.removeChild(textarea);
			}
		}
	};

	const downloadImage = async (imageUrl: string | undefined) => {
		if (!imageUrl) return;
		try {
			const response = await fetch(imageUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `campaign-image-${Date.now()}.jpg`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Error downloading image:', error);
		}
	};
	return (
		<Grid container className="mt-4 flex justify-between gap-x-6 rounded-lg bg-background-paper p-6">
			<Grid item xs={4} className={`rounded-2xl border ${borderColorClass}`}>
				<Box className={`${borderColorClass} flex items-center justify-between border border-x-0 border-t-0 px-4 py-2`}>
					<div className="text-base font-medium">{t(fetchedCampaign?.content_type!)}</div>
					<Divider orientation="horizontal" className="mx-2 w-3/4 bg-grey-100" />
					<img src={getMediaLogo(platform)} alt={`${platform} logo`} className="h-7 w-7" />
				</Box>
				<Box className="flex flex-col justify-between">
					<Box className="mx-2 mt-2">
						{fetchedCampaignContents?.images[0] || fetchedCampaignContents?.sample_image ? (
							<Box className="relative mb-2">
								<img src={fetchedCampaignContents?.images[0] ? fetchedCampaignContents?.images[0] : fetchedCampaignContents?.sample_image} alt={`sample img`} className="h-64 w-full" />
								<Tooltip title={t('download')}>
									<IconButton
										onClick={() => downloadImage(fetchedCampaignContents?.images[0] ? fetchedCampaignContents?.images[0] : fetchedCampaignContents?.sample_image)}
										sx={{
											position: 'absolute',
											top: 8,
											right: 8,
											backgroundColor: 'rgba(0, 0, 0, 0.5)',
											color: 'white',
											'&:hover': {
												backgroundColor: 'rgba(0, 0, 0, 0.7)',
											},
										}}
										size="small"
									>
										<DownloadIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</Box>
						) : (
							<Skeleton animation={false} variant="rectangular" className="mb-2 h-64 w-full" />
						)}
						<Typography className="text-xs font-normal text-text-primary">{fetchedCampaignContents?.caption}</Typography>
						<Typography className="mb-2 text-xs font-normal text-blue-400">{fetchedCampaignContents?.handle_name}</Typography>
						<Box className="flex gap-1">
							{fetchedCampaignContents?.tags?.map((tag, i) => (
								<Typography key={i} className="text-xs font-normal text-secondary-main">
									{tag}
								</Typography>
							))}
						</Box>
						<img src={eimojiGroup} alt={`emoji line`} className="my-2 w-full" />
					</Box>
					<img src={telegramChatInputImg} alt={`chat input img`} className="w-full" />
				</Box>
			</Grid>
			<Grid item className="flex grow flex-col items-end justify-start gap-y-5">
				<Box className="w-full">
					<Box className="mb-2 flex items-center justify-between">
						<Typography variant="caption" className="font-medium text-text-secondary">
							{t('instructions')}
						</Typography>
						<Tooltip title={copied.field === 'description' ? t('copied') : t('copy')}>
							<IconButton size="small" onClick={() => copyToClipboard(fetchedCampaignContents?.description, 'description')} sx={{ p: 0.5 }}>
								{copied.field === 'description' ? <CheckIcon fontSize="small" className="text-success-main" /> : <ContentCopyIcon fontSize="small" className="text-text-secondary transition-colors hover:text-primary-main" />}
							</IconButton>
						</Tooltip>
					</Box>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						disabled
						id="description-field"
						multiline
						rows={4}
						value={fetchedCampaignContents?.description}
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: '#f5f5f5',
								'& fieldset': {
									borderColor: '#e0e0e0',
								},
								'&:hover fieldset': {
									borderColor: '#e0e0e0',
								},
								'&.Mui-disabled fieldset': {
									borderColor: '#e0e0e0',
								},
							},
							'& .MuiOutlinedInput-input.Mui-disabled': {
								color: 'text.primary',
								WebkitTextFillColor: 'inherit',
							},
						}}
					/>
				</Box>

				<Box className="w-full">
					<Box className="mb-2 flex items-center justify-between">
						<Typography variant="caption" className="font-medium text-text-secondary">
							{t(platform === 'instagram' ? 'handle_name' : 'chanel_id')}
						</Typography>
						<Tooltip title={copied.field === 'handle' ? t('copied') : t('copy')}>
							<IconButton size="small" onClick={() => copyToClipboard(fetchedCampaignContents?.handle_name as string, 'handle')} sx={{ p: 0.5 }}>
								{copied.field === 'handle' ? <CheckIcon fontSize="small" className="text-success-main" /> : <ContentCopyIcon fontSize="small" className="text-text-secondary transition-colors hover:text-primary-main" />}
							</IconButton>
						</Tooltip>
					</Box>
					<TextField
						fullWidth
						variant="outlined"
						size="small"
						disabled
						id="handle-field"
						value={fetchedCampaignContents?.handle_name}
						sx={{
							'& .MuiOutlinedInput-root': {
								backgroundColor: '#f5f5f5',
								'& fieldset': {
									borderColor: '#e0e0e0',
								},
								'&:hover fieldset': {
									borderColor: '#e0e0e0',
								},
								'&.Mui-disabled fieldset': {
									borderColor: '#e0e0e0',
								},
							},
							'& .MuiOutlinedInput-input.Mui-disabled': {
								color: 'text.primary',
								WebkitTextFillColor: 'inherit',
							},
						}}
					/>
				</Box>
			</Grid>
		</Grid>
	);
};

export default IsSocialPreview;
